/**
 * Client-safe helpers for surfacing database connectivity errors.
 *
 * Imported from server RSCs and `error.tsx` — must not import Prisma or Node drivers.
 */

/**
 * Shown when this region's Postgres is unreachable (e.g. its container is down).
 * One region = one database, so there is no generic "start the DB" script — start
 * that region's own container. `pnpm db:tenant:status` prints every region's URL.
 */
export const DATABASE_CONNECTION_HINT =
	"Cannot connect to this region's database. Start its Postgres container (`cd packages/tenant-db && REGION=<org>_<region> PORT=<port> docker compose up -d postgres`), then confirm DATABASE_URL in apps/tenant-region-instance/.env matches. `pnpm db:tenant:status` lists every region and its URL.";

/**
 * Shown when Prisma reports missing tables/columns. P2021/P2022 only prove the
 * database and the generated client disagree — NOT which side is stale. A client
 * generated against a different schema (e.g. left behind by a branch switch,
 * since prisma/generated is git-ignored) produces the same codes as an
 * unmigrated database. `db:tenant:sync` fixes either: it runs `migrate deploy`
 * against every registered region, then `generate` once — unlike
 * `db:tenant:migrate`/`:deploy`, which only touch the one DB named in
 * apps/tenant-region-instance/.env.
 */
export const DATABASE_SCHEMA_OUT_OF_DATE_HINT =
	"The database and the Prisma client disagree on the schema. From the repo root run `pnpm db:tenant:sync` — it applies pending migrations to every registered region and regenerates the Prisma client. `pnpm db:tenant:status` shows which region is behind.";

const PRISMA_CONNECTION_CODES: ReadonlySet<string> = new Set([
	"P1000",
	"P1001",
	"P1002",
	"P1017",
	"ECONNREFUSED",
	"ENOTFOUND",
	"ETIMEDOUT",
]);

const PRISMA_SCHEMA_CODES: ReadonlySet<string> = new Set(["P2021", "P2022"]);

const CONNECTION_MESSAGE_REGEX =
	/ECONNREFUSED|ENOTFOUND|ETIMEDOUT|Can't reach database|connection refused|P1000|P1001|P1002|P1017/i;

const SCHEMA_MESSAGE_REGEX =
	/does not exist in the current database|The column `[^`]+` does not exist|column .* does not exist|relation .* does not exist|TableDoesNotExist|ColumnDoesNotExist|P2021|P2022/i;

function readString(obj: unknown, key: string): string | null {
	if (typeof obj !== "object" || obj === null) {
		return null;
	}
	const value = (obj as Record<string, unknown>)[key];
	return typeof value === "string" ? value : null;
}

function forEachErrorInChain(error: unknown, visit: (err: unknown) => void) {
	let current: unknown = error;
	const seen = new Set<unknown>();
	for (let i = 0; i < 8 && current !== undefined && current !== null; i++) {
		if (seen.has(current)) {
			break;
		}
		seen.add(current);
		visit(current);
		if (typeof current !== "object") {
			break;
		}
		current = (current as { cause?: unknown }).cause;
	}
}

export function databaseConnectionUserMessage(error: unknown): string | null {
	let connectionHint: string | null = null;
	let schemaHint: string | null = null;

	forEachErrorInChain(error, (err) => {
		const code = readString(err, "code");
		if (code && PRISMA_CONNECTION_CODES.has(code)) {
			connectionHint = DATABASE_CONNECTION_HINT;
		}
		if (code && PRISMA_SCHEMA_CODES.has(code)) {
			schemaHint = DATABASE_SCHEMA_OUT_OF_DATE_HINT;
		}
		const message = readString(err, "message");
		if (message && CONNECTION_MESSAGE_REGEX.test(message)) {
			connectionHint = DATABASE_CONNECTION_HINT;
		}
		if (message && SCHEMA_MESSAGE_REGEX.test(message)) {
			schemaHint = DATABASE_SCHEMA_OUT_OF_DATE_HINT;
		}
	});

	if (connectionHint) {
		return connectionHint;
	}
	if (schemaHint) {
		return schemaHint;
	}
	return null;
}

/** True when the error is a known DB connectivity or schema drift failure. */
export function isDatabaseUnavailableError(error: unknown): boolean {
	return databaseConnectionUserMessage(error) !== null;
}
